import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./CohortDetails.css";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import leftArrow from "../../../../assests/leftArrow.svg";
import warehouse from "../../../../assests/warehouse.svg";
import cohorts from "../../../../assests/cohorts.svg";
import search from "../../../../assests/search.svg";
import downloadIcon from "../../../../assests/filedownload.svg";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import LoadingSpinner from "../../../Loading/component";
import { useParams } from "react-router-dom";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import cohortVmImg from "../../../../assests/cohortVmImg.svg";
import cohortType from "../../../../assests/cohortType.svg";
import locationIcon from "../../../../assests/locationIcon.svg";
import removeIcon from "../../../../assests/remove.svg";
import awaitingIcon from "../../../../assests/awaitingIcon.svg";
import { Dialog, DialogActions, DialogContent, Checkbox } from "@mui/material";

const CohortDetailsComponent = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [machineSearchQuery, setMachineSearchQuery] = useState("");
  const [selectedVMs, setSelectedVMs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filteredMachines, setFilteredMachines] = useState([]);
  const [commaSearchIds, setCommaSearchIds] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Reset all fields when delete is confirmed
  useEffect(() => {
    if (props.shouldResetFields) {
      resetAllFields();
      props.onResetFieldsComplete();
    }
  }, [props.shouldResetFields]);

  // Function to reset all fields
  const resetAllFields = () => {
    setMachineSearchQuery("");
    setSelectedVMs([]);
    setSelectAll(false);
    setCommaSearchIds([]);
    setFilteredMachines(props.cohortDetailsData?.machines || []);
  };

  // Optimized search function
  const performMachineSearch = useCallback((query) => {
    if (!props.cohortDetailsData?.machines) {
      setFilteredMachines([]);
      return;
    }

    const searchLower = query.toLowerCase().trim();
    
    if (query.includes(',')) {
      const searchIds = query
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');
      
      setCommaSearchIds(searchIds);
      
      const searchIdSet = new Set(searchIds);
      const filtered = [];
      for (const machine of props.cohortDetailsData.machines) {
        const machineId = machine.id.toString();
        if (searchIdSet.has(machineId)) {
          filtered.push(machine);
        }
      }
      setFilteredMachines(filtered);
    } else {
      setCommaSearchIds([]);
      const filtered = [];
      for (const machine of props.cohortDetailsData.machines) {
        const machineId = machine.id.toString();
        const machineName = machine.name ? machine.name.toLowerCase() : '';
        if (machineId.includes(searchLower) || machineName.includes(searchLower)) {
          filtered.push(machine);
        }
      }
      setFilteredMachines(filtered);
    }
  }, [props.cohortDetailsData]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      performMachineSearch(machineSearchQuery);
    }, 150);

    setSearchTimeout(timeout);

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [machineSearchQuery, performMachineSearch]);

  // Update selectedVMs when comma search is used
  useEffect(() => {
    if (commaSearchIds.length > 0 && filteredMachines.length > 0) {
      const filteredIds = filteredMachines.map(machine => machine.id);
      setSelectedVMs(filteredIds);
    }
  }, [commaSearchIds, filteredMachines]);

  // Update selectAll based on selection
  useEffect(() => {
    if (filteredMachines.length > 0 && selectedVMs.length === filteredMachines.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedVMs, filteredMachines]);

  // Handle paste in search box
  const handleMachineSearchPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    if (pastedText.includes(',')) {
      setMachineSearchQuery(pastedText);
      
      const pastedIds = pastedText
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');
      
      if (pastedIds.length > 0) {
        toastr.info(`Searching for ${pastedIds.length} VM ID(s)`);
      }
    } else {
      const newValue = machineSearchQuery + pastedText;
      setMachineSearchQuery(newValue);
    }
  };

  // Function to download CSV from frontend data (no API call)
  const downloadCSVFromFrontend = () => {
    const machines = props.cohortDetailsData?.machines || [];
    
    if (machines.length === 0) {
      toastr.warning("No VM data available to download");
      return;
    }

    setIsDownloading(true);

    try {
      // Define headers
      const headers = ['VM ID', 'VM Name', 'Status'];
      
      // Create CSV rows
      const csvRows = [];
      
      // Add headers
      csvRows.push(headers.join(','));
      
      // Add data rows from frontend data
      for (const machine of machines) {
        const values = [
          machine.id || '',
          machine.name || '',
         
          machine.status || 'N/A'
        ];
        
        // Handle values that contain commas or quotes
        const escapedValues = values.map(value => {
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        
        csvRows.push(escapedValues.join(','));
      }
      
      // Create blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cohort_vm_list_${id}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toastr.success(`Downloaded ${machines.length} VM records successfully`);
    } catch (error) {
      console.error("Download error:", error);
      toastr.error("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBack = () => navigate(-1);
  const handleAddVmDetails = () => navigate(`/home/configuration/cohortadddetails/${id}`);
  const handleAddVm = () => navigate(`/home/configuration/cohortadduser/${id}`);
  const handleAddUser = () => navigate(`/home/configuration/cohortaddvm/${id}`);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleMachineSearchChange = (event) => {
    const value = event.target.value;
    setMachineSearchQuery(value);
  };

  const handleMachineSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (machineSearchQuery.includes(',')) {
        const searchIds = machineSearchQuery
          .split(',')
          .map(id => id.trim())
          .filter(id => id !== '');
        
        if (searchIds.length > 0) {
          toastr.info(`Searching for ${searchIds.length} VM ID(s)`);
        }
      }
    }
  };

  // Memoize filtered users for performance
  const filteredUsers = useMemo(() => {
    if (!props.cohortDetailsData?.users) return [];
    
    const searchLower = searchQuery.toLowerCase();
    return props.cohortDetailsData.users.filter((user) => {
      const userId = user.id.toString();
      const userPhone = user.phone ? user.phone.toString() : "";
      const userName = user.name ? user.name.toLowerCase() : "";
      return userId.includes(searchLower) || userName.includes(searchLower) || userPhone.includes(searchLower);
    });
  }, [props.cohortDetailsData?.users, searchQuery]);

  const handleVMCheckboxChange = (vmId) => {
    setSelectedVMs(prev => {
      if (prev.includes(vmId)) {
        return prev.filter(id => id !== vmId);
      } else {
        return [...prev, vmId];
      }
    });
  };

  const handleSelectAllChange = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allVisibleIds = filteredMachines.map(machine => machine.id);
      setSelectedVMs(allVisibleIds);
    } else {
      setSelectedVMs([]);
    }
  };

  const handleDirectCommaRemove = () => {
    if (machineSearchQuery.includes(',')) {
      const idsToRemove = machineSearchQuery
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '')
        .map(id => parseInt(id))
        .filter(id => !isNaN(id));
      
      if (idsToRemove.length === 0) {
        toastr.warning("Please enter valid VM IDs separated by commas");
        return;
      }
      props.handleVmDeleteClick(idsToRemove);
    } else if (machineSearchQuery.trim() !== '') {
      const singleId = parseInt(machineSearchQuery.trim());
      if (!isNaN(singleId)) {
        props.handleVmDeleteClick([singleId]);
      } else {
        toastr.warning("Please enter a valid VM ID");
      }
    } else if (selectedVMs.length > 0) {
      props.handleVmDeleteClick(selectedVMs);
    } else {
      toastr.warning("Please select VMs or enter VM IDs to remove");
    }
  };

  const handleClearSearch = () => {
    setMachineSearchQuery("");
    setSelectedVMs([]);
    setSelectAll(false);
    setCommaSearchIds([]);
  };

  const getCommaSearchStatus = () => {
    if (commaSearchIds.length > 0 && filteredMachines.length > 0) {
      return `Found ${filteredMachines.length} VM(s) matching ${commaSearchIds.length} ID(s)`;
    }
    if (commaSearchIds.length > 0 && filteredMachines.length === 0) {
      return `No VMs found for the entered IDs`;
    }
    return null;
  };

  const getRemoveButtonText = () => {
    if (machineSearchQuery.includes(',') && machineSearchQuery.trim() !== '') {
      const ids = machineSearchQuery
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '');
      return `Remove (${ids.length} IDs)`;
    } else if (selectedVMs.length > 0) {
      return `Remove Selected (${selectedVMs.length})`;
    } else if (machineSearchQuery.trim() !== '' && !machineSearchQuery.includes(',')) {
      return `Remove (1 ID)`;
    }
    return "Remove";
  };

  const isRemoveButtonEnabled = () => {
    return (
      (machineSearchQuery.includes(',') && machineSearchQuery.trim() !== '') ||
      (machineSearchQuery.trim() !== '' && !machineSearchQuery.includes(',')) ||
      selectedVMs.length > 0
    );
  };

  return (
    <div className="cohort-details-container">
      <Sidebar />

      {props.loading ? (
        <LoadingSpinner />
      ) : (
        <div className="cohort-details-content">
          <div className="cohort-header">
            <img src={leftArrow} alt="Back" className="back-icon" onClick={handleBack} />
            <h2 className="cohort-title">Cohort ID: {props.cohortDetailsData?.id}</h2>
          </div>

          <div className="cohort-sections-container">
            {/* Cohort Basic Info Section */}
            <div className="cohort-basic-info">
              <div className="section-header">
                <p className="section-title">
                  <img src={warehouse} alt="" /> Cohort Details
                </p>
                <Button variant="outline-primary" className="edit-button" onClick={handleAddVmDetails}>
                  Edit
                </Button>
              </div>

              <div className="cohort-info-card">
                <div className="cohort-profile">
                  <img src={cohortVmImg} alt="Cohort" className="cohort-image" />
                  <div className="cohort-info-text">
                    <p className="cohort-name">{props.cohortDetailsData?.name}</p>
                  </div>
                </div>
                <div className="cohort-details">
                  <div className="detail-row">
                    <img src={cohortType} alt="Cohort Type" className="detail-icon" />
                    <p>Cohort type: {props.cohortDetailsData?.cohortTypeName}</p>
                  </div>
                  <div className="detail-row">
                    <img src={locationIcon} alt="Location" className="detail-icon" />
                    <p>Area Name: {props.cohortDetailsData?.areaName}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* User List Section */}
            <div className="cohort-user-list">
              <div className="section-header">
                <p className="section-title">
                  <img src={warehouse} alt="" /> User List
                </p>
                <Button variant="outline-primary" className="edit-button" onClick={handleAddVm}>
                  Edit
                </Button>
              </div>

              <div className="cohort-search-container">
                <div className="cohort-search-wrapper">
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="cohort-search-input"
                    placeholder="Search by User ID or User Name"
                  />
                  <img src={search} alt="search" className="cohort-search-icon" />
                </div>
              </div>

              <div className="table-fixed-container">
                <div className="table-scroll-wrapper">
                  <Table striped hover className="custom-table">
                    <thead className="table-header">
                      <tr>
                        <th className="table-cell">User ID</th>
                        <th className="table-cell">User Name</th>
                        <th className="table-cell">Contact No</th>
                        <th className="table-cell action-cell">Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((item) => (
                          <tr key={item.id} className="data-row">
                            <td className="table-cell">{item.id}</td>
                            <td className="table-cell">{item.name}</td>
                            <td className="table-cell">{item.phone}</td>
                            <td className="table-cell action-cell">
                              <img
                                src={removeIcon}
                                alt="Delete"
                                className="remove-icon"
                                onClick={() => props.handleDeleteClick(item.id)}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="no-data-cell">
                            No User found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>

          {/* Vending Machine List Section */}
          <div className="cohort-vm-list">
            <div className="section-header">
              <div className="vm-header-left">
                <p className="section-title">
                  <img src={cohorts} alt="" /> Vending Machine List
                </p>
                <div className="total-vm-count">
                  Total VMs: {props.cohortDetailsData?.machineCount || "0"}
                </div>
              </div>
              <div className="vm-header-right">
                {/* Download Button - No API Call, Frontend CSV Download */}
                <Button
                  variant="outline-primary"
                  className="download-vm-btn"
                  onClick={downloadCSVFromFrontend}
                  disabled={props.loading || props.cohortDetailsData?.machineCount === 0 || isDownloading}
                >
                 
                  {isDownloading ? "Downloading..." : "Download VM List"}
                </Button>
                <div className="selected-count-container">
                  <Button
                    variant="danger"
                    className="batch-remove-btn"
                    onClick={handleDirectCommaRemove}
                    disabled={props.loading || !isRemoveButtonEnabled()}
                  >
                    {props.loading ? "Removing..." : getRemoveButtonText()}
                  </Button>
                </div>
                <Button variant="outline-primary" className="edit-button" onClick={handleAddUser}>
                  Edit
                </Button>
              </div>
            </div>

            <div className="cohort-search-container">
              <div className="cohort-search-wrapper">
                <input
                  type="search"
                  value={machineSearchQuery}
                  onChange={handleMachineSearchChange}
                  onPaste={handleMachineSearchPaste}
                  onKeyPress={handleMachineSearchKeyPress}
                  className="cohort-search-input"
                  placeholder="Type or paste VM IDs (comma-separated) and click Remove"
                />
                <img src={search} alt="search" className="cohort-search-icon" />
                {(machineSearchQuery || selectedVMs.length > 0) && (
                  <button 
                    className="clear-search-btn"
                    onClick={handleClearSearch}
                    title="Clear search and selection"
                  >
                    Clear
                  </button>
                )}
              </div>
              
              {machineSearchQuery.includes(',') && (
                <div className="paste-mode-indicator">
                  <small>Comma-separated search mode active</small>
                </div>
              )}
              
              {getCommaSearchStatus() && (
                <div className="search-status-indicator">
                  <small>{getCommaSearchStatus()}</small>
                </div>
              )}
            </div>

            <div className="table-fixed-container">
              <div className="table-scroll-wrapper">
                <Table striped hover className="custom-table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-cell checkbox-header-cell">
                        <div className="checkbox-wrapper">
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAllChange}
                            indeterminate={selectedVMs.length > 0 && selectedVMs.length < filteredMachines.length}
                            disabled={props.loading || filteredMachines.length === 0}
                          />
                        </div>
                      </th>
                      <th className="table-cell">V.M ID</th>
                      <th className="table-cell">V.M Name</th>
                      <th className="table-cell">City</th>
                      <th className="table-cell">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMachines.length > 0 ? (
                      filteredMachines.map((item) => (
                        <tr 
                          key={item.id} 
                          className={selectedVMs.includes(item.id) ? "selected-row" : ""}
                        >
                          <td className="table-cell checkbox-body-cell">
                            <div className="checkbox-wrapper">
                              <Checkbox
                                checked={selectedVMs.includes(item.id)}
                                onChange={() => handleVMCheckboxChange(item.id)}
                                disabled={props.loading}
                              />
                            </div>
                          </td>
                          <td className="table-cell">{item.id}</td>
                          <td className="table-cell">{item.name}</td>
                          <td className="table-cell">{item.area_name}</td>
                          <td className="table-cell">{item.status}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="no-data-cell">
                          {machineSearchQuery.includes(',') 
                            ? `No V.M found matching the searched IDs: ${machineSearchQuery}`
                            : "No V.M found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>

          {/* Delete Confirmation Dialog */}
          <DeleteConfirmationDialog
            open={props.showDeleteModal}
            onClose={() => props.setShowDeleteModal(false)}
            onConfirm={props.confirmDelete}
            message={props.deleteMessage}
            deleting={props.deleting}
          />
        </div>
      )}
    </div>
  );
};

const DeleteConfirmationDialog = ({ open, onClose, onConfirm, message, deleting }) => (
  <Dialog
    open={open}
    onClose={!deleting ? onClose : undefined}
    className="delete-dialog"
  >
    <DialogContent className="dialog-content">
      <div className="dialog-icon-container">
        <img src={awaitingIcon} alt="Warning" className="dialog-icon" />
      </div>
      <h5 className="dialog-message-cohort">{message}</h5>
    </DialogContent>
    <DialogActions className="dialog-actions">
      <Button
        variant="outlined"
        onClick={onClose}
        className="cohort-details-cancel-button"
        disabled={deleting}
      >
        No
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        className="cohort-details-confirm-button"
        disabled={deleting}
      >
        {deleting ? "Removing..." : "Yes"}
      </Button>
    </DialogActions>
  </Dialog>
);

export default CohortDetailsComponent;